import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { CreditCard, Wallet } from 'lucide-react';
import ConfirmationModal from '../components/ConfirmationModal';

const Checkout = () => {
  const { user, fetchUser } = useAuth();
  const { cart, getCartTotal, clearCart } = useCart();
  const { t } = useLanguage();
  const [paymentMethod, setPaymentMethod] = useState('cash_wallet');
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();

  const cartTotal = getCartTotal();
  const canPayWithCashWallet = user?.cash_wallet >= cartTotal;
  const canPayWithMixed = (user?.cash_wallet + user?.commission_wallet) >= cartTotal;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);

    try {
      // Create order
      const orderData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          product_type: item.product_type,
          name: item.details?.name,
          price: item.details?.price,
          quantity: item.quantity || 1
        })),
        payment_method: paymentMethod
      };

      const orderResponse = await api.post('/orders', orderData);
      const order = orderResponse.data;

      if (paymentMethod === 'stripe') {
        // Create Stripe checkout session
        const stripeResponse = await api.post('/payments/stripe/create-session', {
          amount: getCartTotal(),
          origin_url: window.location.origin,
          order_id: order.id
        });

        // Redirect to Stripe
        window.location.href = stripeResponse.data.url;
      } else if (paymentMethod === 'cash_wallet' || paymentMethod === 'mixed_wallet') {
        // Wallet payment completed instantly - refresh user data to show updated balance
        if (fetchUser) await fetchUser();
        toast.success('Payment successful! Your order is confirmed. Wallet balance updated.');
        await clearCart();
        navigate('/my-orders');
      } else if (paymentMethod === 'manual') {
        toast.success('Order created! Please contact us for payment instructions.');
        await clearCart();
        navigate('/my-orders');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please login to checkout</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" data-testid="checkout-title">Checkout</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold mb-4">Order Summary</h2>
            
            {cart.map((item) => (
              <div key={item.id} className="glass-card p-4 flex gap-4">
                <div className="flex-1">
                  <h3 className="font-semibold">{item.details?.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {item.product_type === 'product' ? 'Product' : 'Course'}
                  </p>
                </div>
                <p className="font-bold text-primary">₹{item.details?.price}</p>
              </div>
            ))}

            <div className="glass-card p-6">
              <div className="flex justify-between text-xl font-bold">
                <span>Total</span>
                <span className="text-primary" data-testid="checkout-total">
                  ₹{getCartTotal().toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div>
            <h2 className="text-2xl font-semibold mb-4">Payment Method</h2>
            
            <div className="space-y-3">
              {/* Cash Wallet Payment */}
              <label
                className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all ${
                  paymentMethod === 'cash_wallet' ? 'border-primary' : 'border-transparent'
                } ${!canPayWithCashWallet ? 'opacity-50' : ''}`}
                data-testid="payment-cash-wallet"
              >
                <input
                  type="radio"
                  name="payment"
                  value="cash_wallet"
                  checked={paymentMethod === 'cash_wallet'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={!canPayWithCashWallet}
                  className="w-5 h-5"
                />
                <Wallet className="w-6 h-6 text-primary" />
                <div className="flex-1">
                  <p className="font-semibold">Cash Wallet (100%)</p>
                  <p className="text-sm text-muted-foreground">
                    Balance: ₹{user?.cash_wallet?.toFixed(2)} 
                    {!canPayWithCashWallet && ' - Insufficient'}
                  </p>
                </div>
              </label>

              {/* Mixed Wallet Payment */}
              <label
                className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all ${
                  paymentMethod === 'mixed_wallet' ? 'border-primary' : 'border-transparent'
                } ${!canPayWithMixed ? 'opacity-50' : ''}`}
                data-testid="payment-mixed-wallet"
              >
                <input
                  type="radio"
                  name="payment"
                  value="mixed_wallet"
                  checked={paymentMethod === 'mixed_wallet'}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  disabled={!canPayWithMixed}
                  className="w-5 h-5"
                />
                <Wallet className="w-6 h-6 text-secondary" />
                <div className="flex-1">
                  <p className="font-semibold">Mixed Wallet (50% + 50%)</p>
                  <p className="text-sm text-muted-foreground">
                    50% Cash (₹{(cartTotal/2).toFixed(2)}) + 50% Commission (₹{(cartTotal/2).toFixed(2)})
                  </p>
                  {!canPayWithMixed && (
                    <p className="text-xs text-destructive">Insufficient combined balance</p>
                  )}
                </div>
              </label>

              {settings?.payment_gateways?.stripe && (
                <label
                  className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all ${
                    paymentMethod === 'stripe' ? 'border-primary' : 'border-transparent'
                  }`}
                  data-testid="payment-stripe"
                >
                  <input
                    type="radio"
                    name="payment"
                    value="stripe"
                    checked={paymentMethod === 'stripe'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-5 h-5"
                  />
                  <CreditCard className="w-6 h-6 text-primary" />
                  <div className="flex-1">
                    <p className="font-semibold">Stripe</p>
                    <p className="text-sm text-muted-foreground">Credit/Debit Card</p>
                  </div>
                </label>
              )}

              {settings?.payment_gateways?.razorpay && (
                <label
                  className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all ${
                    paymentMethod === 'razorpay' ? 'border-primary' : 'border-transparent'
                  }`}
                  data-testid="payment-razorpay"
                >
                  <input
                    type="radio"
                    name="payment"
                    value="razorpay"
                    checked={paymentMethod === 'razorpay'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-5 h-5"
                  />
                  <Wallet className="w-6 h-6 text-secondary" />
                  <div className="flex-1">
                    <p className="font-semibold">Razorpay</p>
                    <p className="text-sm text-muted-foreground">UPI, Cards, Net Banking</p>
                  </div>
                </label>
              )}

              {settings?.payment_gateways?.manual && (
                <label
                  className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all ${
                    paymentMethod === 'manual' ? 'border-primary' : 'border-transparent'
                  }`}
                  data-testid="payment-manual"
                >
                  <input
                    type="radio"
                    name="payment"
                    value="manual"
                    checked={paymentMethod === 'manual'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-5 h-5"
                  />
                  <Wallet className="w-6 h-6 text-accent" />
                  <div className="flex-1">
                    <p className="font-semibold">Manual Payment</p>
                    <p className="text-sm text-muted-foreground">UPI / QR Code / Bank Transfer</p>
                  </div>
                </label>
              )}
            </div>

            {/* Manual Payment Instructions */}
            {paymentMethod === 'manual' && settings && (
              <div className="glass-card p-6 mt-4">
                <h3 className="text-lg font-semibold mb-4">Payment Instructions</h3>
                
                {settings.manual_payment_upi && (
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">UPI ID:</p>
                    <div className="p-3 bg-white/5 rounded-lg font-mono text-primary">
                      {settings.manual_payment_upi}
                    </div>
                  </div>
                )}
                
                {settings.manual_payment_qr && (
                  <div className="mb-4">
                    <p className="text-sm font-medium mb-2">Scan QR Code to Pay:</p>
                    <img 
                      src={settings.manual_payment_qr} 
                      alt="Payment QR Code" 
                      className="w-64 h-64 object-contain bg-white rounded-lg p-4"
                    />
                  </div>
                )}
                
                <div className="mt-4 p-4 bg-accent/10 rounded-lg">
                  <p className="text-sm text-accent font-medium">
                    📱 After payment, please contact us on WhatsApp ({settings.whatsapp_number}) with payment screenshot
                  </p>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowConfirmation(true)}
              disabled={loading || cart.length === 0}
              className="w-full btn-primary mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="place-order-button"
            >
              {loading ? 'Processing...' : `Pay ₹${getCartTotal().toFixed(2)}`}
            </button>
          </div>
        </div>
      </div>

      {/* Double Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={handleCheckout}
        title={t('confirmPayment')}
        message={`${t('confirmPaymentMsg')} Total: ₹${getCartTotal().toFixed(2)}`}
        confirmText={t('confirm')}
        cancelText={t('cancel')}
        type="warning"
      />
    </div>
  );
};

export default Checkout;
